"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader, ArrowRight, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InquiryPortal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="153016" id="request-your-quote" className="py-24 bg-background relative overflow-hidden text-foreground" ref={ref}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/3 rounded-full blur-3xl pointer-events-none" />

      <div className={`container mx-auto px-4 max-w-4xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-default font-semibold tracking-tight text-primary mb-4">
            Request Your Quote
          </h2>
          <p className="text-foreground/70 font-sans text-lg max-w-xl mx-auto">
            A thoughtful cleaning experience starts here. Tell us about your home and find your luxury of time.
          </p>
        </div>

        <div className="bg-card text-card-foreground rounded-[24px] shadow-xl shadow-primary/5 border border-border/40 p-6 md:p-10">
          <FormProvider formKey="quote_form_home" formId="quote_form_home" sectionName="Request Your Quote" defaultValues={{
          cleaning_type: "",
          property_type: "",
          bedrooms_bathrooms: "",
          square_footage: "",
          frequency: "",
          service_area: "",
          service_address: "",
          zip_code: "",
          pets: "",
          extra_services: "",
          notes: "",
          preferred_date: "",
          date_flexibility: "",
          full_name: "",
          phone_number: "",
          email_address: ""
        }} validationRules={{
          cleaning_type: {
            required: true
          },
          property_type: {
            required: true
          },
          bedrooms_bathrooms: {
            required: true
          },
          square_footage: {
            required: true
          },
          frequency: {
            required: true
          },
          service_area: {
            required: true
          },
          service_address: {
            required: true
          },
          zip_code: {
            required: true,
            pattern: "^[0-9]{5}(?:-[0-9]{4})?$"
          },
          preferred_date: {
            required: true
          },
          full_name: {
            required: true
          },
          phone_number: {
            required: true,
            pattern: "phone"
          },
          email_address: {
            required: true,
            pattern: "email"
          }
        }} submitText="Get My Quote" successMessage="Thank you! We've received your details and will provide a custom quote shortly.">
            <MultiStepForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [activeStep, setActiveStep] = useState("step-1");
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold mb-2">Quote Request Sent</h3>
        <p className="text-foreground/70 font-sans mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-full px-8 border-primary text-primary hover:bg-primary/5 font-sans">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <Tabs value={activeStep} onValueChange={setActiveStep} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-10 bg-secondary/50 p-1 rounded-full">
          <TabsTrigger value="step-1" className="rounded-full font-sans py-2.5 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">
            1. Service
          </TabsTrigger>
          <TabsTrigger value="step-2" className="rounded-full font-sans py-2.5 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">
            2. Location
          </TabsTrigger>
          <TabsTrigger value="step-3" className="rounded-full font-sans py-2.5 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm">
            3. Details
          </TabsTrigger>
        </TabsList>

        <div className="min-h-[400px]">
          {}
          <TabsContent value="step-1" className="space-y-6 mt-0 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="cleaning_type" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Cleaning Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select cleaning type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["Standard Cleaning", "Deep Cleaning", "Move‑In / Move‑Out Cleaning", "Organizing", "Renovation Cleaning", "Commercial Cleaning"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="property_type" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Property Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["House", "Apartment", "Condo", "Townhouse", "Office", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="bedrooms_bathrooms" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Bedrooms / Bathrooms</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 3 Bedrooms, 2 Bathrooms" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="square_footage" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Approx. Square Footage</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="e.g., 1500" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="frequency" render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-sans text-foreground/80">Frequency</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["One‑time", "Weekly", "Bi‑weekly", "Monthly"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="flex justify-end pt-6">
              <Button type="button" onClick={() => setActiveStep("step-2")} className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-sans shadow-lg shadow-primary/10">
                Continue <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="step-2" className="space-y-6 mt-0 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="service_area" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Service Area / Neighborhood</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Winter Park" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="zip_code" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Zip Code</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 32789" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="service_address" render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-sans text-foreground/80">Service Address</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Street address, suite, etc." className="min-h-[80px] rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="pets" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Pets</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select pet situation" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["No pets", "Dogs", "Cats", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="extra_services" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Extra Services</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select extra services" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["Inside Fridge", "Inside Oven", "Window Cleaning", "Carpet Cleaning", "Laundry"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs">Select any primary extra service needed</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="flex justify-between pt-6">
              <Button type="button" variant="ghost" onClick={() => setActiveStep("step-1")} className="rounded-full font-sans text-foreground/60 hover:text-primary hover:bg-secondary">
                Back
              </Button>
              <Button type="button" onClick={() => setActiveStep("step-3")} className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-sans shadow-lg shadow-primary/10">
                Continue <ArrowRight className="ml-2 size-4" />
              </Button>
            </div>
          </TabsContent>

          {}
          <TabsContent value="step-3" className="space-y-6 mt-0 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="preferred_date" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Preferred Date</FormLabel>
                    <FormControl>
                      <Input type="date" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="date_flexibility" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Date Flexibility</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 rounded-xl border-border bg-background focus:ring-primary/20">
                          <SelectValue placeholder="Select flexibility" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-xl border-border">
                        {["Exact date", "± 1 day", "± 3 days", "Flexible"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>{option}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="full_name" render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-sans text-foreground/80">Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Doe" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone_number" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">Phone Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(407) 717-9881" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email_address" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-sans text-foreground/80">LC121108819@gmail.com</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="lc121108819@gmail.com" className="h-12 rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="notes" render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-sans text-foreground/80">Notes</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Additional details or special instructions" className="min-h-[100px] rounded-xl border-border bg-background focus:ring-primary/20" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm font-sans">
                {submitError}
              </div>}

            <div className="flex justify-between pt-6 border-t border-border/40">
              <Button type="button" variant="ghost" onClick={() => setActiveStep("step-2")} className="rounded-full font-sans text-foreground/60 hover:text-primary hover:bg-secondary">
                Back
              </Button>
              <Button type="submit" disabled={isSubmitting} className="rounded-full px-8 min-w-[160px] bg-primary text-primary-foreground hover:bg-primary/90 font-sans shadow-lg shadow-primary/10">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 size-4" />
                    Calculating...
                  </> : "Get My Quote"}
              </Button>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </Form>;
}